import { withNamespaces } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { withRouter, useHistory } from "react-router-dom";
import {
  Container,
  Col,
  Row,
  Card,
  CardBody,
  Alert,
  Form,
  FormGroup,
  Label,
  Input,
  UncontrolledTooltip,
  Button,
  Spinner,
} from "reactstrap";
import { connect } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  TwitterWizard,
  TwitterWizardNavItem,
} from "src/modules/data-protection/components/TwitterWizard/index.jsx";
import Navigator from "src/modules/data-protection/components/navigator/index.jsx";
import { Link } from "react-router-dom";
import Select from "react-select";
import CommonService from "src/modules/data-protection/apis/CommonService";
import DataProtectionTomsService from "src/modules/data-protection/apis/DataProtectionTomsService";

const DataProtectionCreateTOM = ({ t, Organization, token }) => {
  const history = useHistory();

  const [activeTab, setActiveTab] = useState("basic-information");
  const [selectedLegalEntities, setSelectedLegalEntities] = useState([]);
  const [name, setName] = useState("");
  const [selectedAssignedAnalyst, setSelectedAssignedAnalyst] = useState(null);
  const [editableSections, setEditableSections] = useState([]);
  const [useDefaultTexts, setUseDefaultTexts] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [completedSectionIndexes, setCompletedSectionIndexes] = useState([]);
  const [errors, setErrors] = useState({
    name: [],
    legalEntities: [],
  });

  const TOM_SECTION_INFO = {
    Confidentiality:
      "Measures that prevent unauthorized access to personal data, systems, rooms, devices, and communication channels.",

    Integrity:
      "Measures that ensure personal data remains accurate, complete, and protected from unauthorized modification or corruption.",

    "Availability and resilience":
      "Measures that ensure systems and data remain accessible, recoverable, and operational during incidents, failures, or disruptions.",

    "Procedures for regular review and evaluation":
      "Processes used to periodically assess whether TOMs remain effective, documented, updated, and compliant.",
  };

  const wizardSteps = useMemo(() => {
    const steps = [
      {
        key: "basic-information",
        number: "01",
        title: t("Basic Information"),
      },
    ];

    if (useDefaultTexts === false) {
      steps.push({
        key: "edit-texts",
        number: "02",
        title: t("Edit Texts"),
      });
    }

    steps.push({
      key: "finalize",
      number: useDefaultTexts === false ? "03" : "02",
      title: t("Finalize"),
    });

    return steps;
  }, [t, useDefaultTexts]);

  const handleMeasureInputKeyDown = (
    e,
    sectionIndex,
    subsectionIndex,
    measureType
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddMeasure(sectionIndex, subsectionIndex, measureType);
    }
  };

  const {
    isFetching: legalEntetiesFetching,
    data: legalEnteties,
    isError: legalEntitiesError,
  } = useQuery({
    queryKey: ["gdpr-fetch-organization-legal-entities", Organization.id],
    queryFn: async () => {
      const service = DataProtectionTomsService.getInstance();
      return await service.legalEntitiesWithouTOM(Organization?.id);
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while fetching legal entities."), {
        type: "error",
      });
    },
  });

  const {
    isFetching: analystsFetching,
    data: analysts,
    isError: analystsError,
  } = useQuery({
    queryKey: ["gdpr-fetch-analysts-and-admins"],
    queryFn: async () => {
      const service = CommonService.getInstance();

      return await service.analystAdminsAndAnalysts();
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while fetching users."), {
        type: "error",
      });
    },
  });

  const {
    isFetching: tomDefaultsFetching,
    data: tomDefaults,
    isError: tomDefaultsError,
  } = useQuery({
    queryKey: ["tom-defaults"],
    queryFn: async () => {
      const service = DataProtectionTomsService.getInstance();
      return await service.tomDefaults();
    },
    enabled: activeTab === "edit-texts",
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while fetching TOM default texts."), {
        type: "error",
      });
    },
  });

  const createTomMutation = useMutation({
    mutationFn: async (payload) => {
      const service = DataProtectionTomsService.getInstance();
      return await service.create(payload);
    },
    onSuccess: (response) => {
      toast(t("TOM created successfully."), {
        type: "success",
      });
    },
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while creating the TOM."), {
        type: "error",
      });
    },
  });

  const validateBasicInfo = () => {
    const nextErrors = {
      name: [],
      legalEntities: [],
    };
    let isValid = true;

    if (!name.trim()) {
      nextErrors.name = [t("Please enter a name.")];
      isValid = false;
    }

    if (!selectedLegalEntities?.length) {
      nextErrors.legalEntities = [t("Please select at least one legal entity.")];
      isValid = false;
    }

    setErrors(nextErrors);
    return { isValid, nextErrors };
  };

  const handleBasicInfoNext = () => {
    const { isValid, nextErrors } = validateBasicInfo();
    if (!isValid) {
      const firstError = nextErrors.name[0] || nextErrors.legalEntities[0];
      if (firstError) {
        toast(firstError, { type: "error" });
      }
      return;
    }

    if (useDefaultTexts === null) {
      toast(t("Please choose whether to use default TOM texts."), {
        type: "error",
      });
      return;
    }

    if (useDefaultTexts === true) {
      setActiveTab("finalize");
    } else {
      setActiveTab("edit-texts");
    }
  };

  const handleNewMeasureInputChange = (
    sectionIndex,
    subsectionIndex,
    measureType,
    value
  ) => {
    setEditableSections((prev) =>
      prev.map((section, sIdx) => {
        if (sIdx !== sectionIndex) return section;

        return {
          ...section,
          subsections: section.subsections.map((subsection, subIdx) => {
            if (subIdx !== subsectionIndex) return subsection;

            const inputKey =
              measureType === "technical"
                ? "new_technical_measure"
                : "new_organizational_measure";

            return {
              ...subsection,
              [inputKey]: value,
            };
          }),
        };
      })
    );
  };

  const handleInformationChange = (sectionIndex, subsectionIndex, value) => {
    setEditableSections((prev) =>
      prev.map((section, sIdx) => {
        if (sIdx !== sectionIndex) return section;

        return {
          ...section,
          subsections: section.subsections.map((subsection, subIdx) => {
            if (subIdx !== subsectionIndex) return subsection;

            return {
              ...subsection,
              information: value,
            };
          }),
        };
      })
    );
  };

  const handleAddMeasure = (sectionIndex, subsectionIndex, measureType) => {
    setEditableSections((prev) =>
      prev.map((section, sIdx) => {
        if (sIdx !== sectionIndex) return section;

        return {
          ...section,
          subsections: section.subsections.map((subsection, subIdx) => {
            if (subIdx !== subsectionIndex) return subsection;

            const listKey =
              measureType === "technical"
                ? "technical_measures"
                : "organizational_measures";

            const inputKey =
              measureType === "technical"
                ? "new_technical_measure"
                : "new_organizational_measure";

            const newValue = subsection[inputKey] || "";

            if (!newValue) {
              toast(t("Please enter a measure title."), { type: "error" });
              return subsection;
            }

            return {
              ...subsection,
              [listKey]: [...(subsection[listKey] || []), newValue],
              [inputKey]: "",
            };
          }),
        };
      })
    );
  };

  const handleRemoveMeasure = (
    sectionIndex,
    subsectionIndex,
    measureType,
    measureIndex
  ) => {
    setEditableSections((prev) =>
      prev.map((section, sIdx) => {
        if (sIdx !== sectionIndex) return section;

        return {
          ...section,
          subsections: section.subsections.map((subsection, subIdx) => {
            if (subIdx !== subsectionIndex) return subsection;

            const key =
              measureType === "technical"
                ? "technical_measures"
                : "organizational_measures";

            return {
              ...subsection,
              [key]: subsection[key].filter((_, mIdx) => mIdx !== measureIndex),
            };
          }),
        };
      })
    );
  };

  const handleSectionNext = () => {
    const isLastSection = activeSectionIndex === editableSections.length - 1;

    setCompletedSectionIndexes((prev) =>
      prev.includes(activeSectionIndex) ? prev : [...prev, activeSectionIndex]
    );

    if (isLastSection) {
      setActiveTab("finalize");
      return;
    }

    setActiveSectionIndex((prev) => prev + 1);
  };

  const handleSectionTabClick = (sectionIndex) => {
    setActiveSectionIndex(sectionIndex);
  };

  const isSectionCompleted = (sectionIndex) =>
    completedSectionIndexes.includes(sectionIndex);

  const handleDescriptionChange = (sectionIndex, subsectionIndex, value) => {
    setEditableSections((prev) =>
      prev.map((section, sIdx) => {
        if (sIdx !== sectionIndex) return section;

        return {
          ...section,
          subsections: section.subsections.map((subsection, subIdx) => {
            if (subIdx !== subsectionIndex) return subsection;

            return {
              ...subsection,
              description: value,
            };
          }),
        };
      })
    );
  };

  const buildCreatePayload = () => {
    const payload = {
      legal_entity_ids: selectedLegalEntities.map((item) => String(item.value)),
      name: name.trim(),
    };

    if (selectedAssignedAnalyst?.value) {
      payload.assigned_analyst_id = selectedAssignedAnalyst.value;
    }

    if (useDefaultTexts === false) {
      payload.initial_texts = buildInitialTextsPayload();
    }

    return payload;
  };

  const buildInitialTextsPayload = () => {
    return editableSections.reduce((sectionsAcc, section) => {
      const subsectionMap = (section.subsections || []).reduce(
        (subAcc, subsection) => {
          subAcc[String(subsection.subsection_index)] = {
            information: subsection.information || "",
            description: subsection.description || "",
            technical_measures: (subsection.technical_measures || []).filter(
              (item) => item.trim() !== ""
            ),
            organizational_measures: (
              subsection.organizational_measures || []
            ).filter((item) => item.trim() !== ""),
          };

          return subAcc;
        },
        {}
      );

      sectionsAcc[String(section.section_index)] = subsectionMap;
      return sectionsAcc;
    }, {});
  };

  const renderBasicInfo = () => {
    return (
      <Form>
        <Row className="mb-3">
          <Col xs="12" lg="4">
            <FormGroup>
              <Label className={errors.legalEntities.length ? "text-danger" : ""}>
                {t("Legal Entities")}
              </Label>
              <span className="text-danger">*</span>
              <Select
                isMulti
                placeholder={t("Select") + "..."}
                options={legalEnteties?.map((l) => ({
                  label: l.title,
                  value: l.id,
                }))}
                isError={legalEntitiesError}
                value={selectedLegalEntities}
                onChange={(items) => {
                  setSelectedLegalEntities(items || []);
                  setErrors((prev) => ({ ...prev, legalEntities: [] }));
                }}
              />
              {errors.legalEntities.map((error) => (
                <small key={error} className="text-danger">
                  {error}
                </small>
              ))}
            </FormGroup>
          </Col>
          <Col xs="12" lg="4">
            <FormGroup>
              <Label className={errors.name.length ? "text-danger" : ""}>
                {t("Name / Label")}
              </Label>
              <span className="text-danger">*</span>
              <Input
                type="text"
                value={name}
                invalid={errors.name.length > 0}
                onChange={(e) => {
                  setName(e.target.value);
                  setErrors((prev) => ({ ...prev, name: [] }));
                }}
              />
              {errors.name.map((error) => (
                <small key={error} className="text-danger">
                  {error}
                </small>
              ))}
            </FormGroup>
          </Col>
          <Col xs="12" lg="4">
            <FormGroup>
              <Label>{t("Assigned Analyst")}</Label>
              <Select
                className="ext-capitalize"
                placeholder={t("Select") + "..."}
                id="assignedAnalyst"
                name="assignedAnalyst"
                options={analysts?.map((analyst) => ({
                  label: analyst?.first_name + " " + analyst?.last_name,
                  value: analyst?.id,
                }))}
                isLoading={analystsFetching}
                isError={analystsError}
                menuPortalTarget={document.body}
                isDisabled={analystsFetching}
                value={selectedAssignedAnalyst}
                onChange={setSelectedAssignedAnalyst}
                isClearable
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col xs="12" lg="4">
            <FormGroup tag="fieldset">
              <Label className="d-block">{t("Choose TOM Setup Type:")}</Label>

              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="useDefaultTexts"
                    checked={useDefaultTexts === true}
                    onChange={() => {
                      setUseDefaultTexts(true);
                      setEditableSections([]);
                      setActiveSectionIndex(0);
                      setCompletedSectionIndexes([]);
                    }}
                  />{" "}
                  {t("Use default TOM texts")}
                </Label>
              </FormGroup>

              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="useDefaultTexts"
                    checked={useDefaultTexts === false}
                    onChange={() => {
                      setUseDefaultTexts(false);
                      setActiveSectionIndex(0);
                      setCompletedSectionIndexes([]);
                    }}
                  />{" "}
                  {t("Customize TOM texts")}
                </Label>
              </FormGroup>
            </FormGroup>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col xs="12" className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleBasicInfoNext}
              disabled={legalEntetiesFetching || analystsFetching}
            >
              {t("Next")}
            </button>
          </Col>
        </Row>
      </Form>
    );
  };

  const getTomSectionInfo = (sectionTitle) => {
    return TOM_SECTION_INFO[sectionTitle] || "";
  };

  const renderEditTexts = () => {
    if (tomDefaultsFetching) {
      return (
        <div className="text-center py-4">
          {t("Loading TOM default texts...")}
        </div>
      );
    }

    if (!editableSections.length) {
      return (
        <div className="text-center py-4">
          {t("No TOM sections available.")}
        </div>
      );
    }

    const activeSection = editableSections[activeSectionIndex];

    return (
      <>
        {/* Section Navigation */}
        <div className="d-flex flex-wrap align-items-center gap-4 mb-4 border-bottom pb-2">
          {editableSections.map((section, sectionIndex) => {
            const isActive = sectionIndex === activeSectionIndex;
            const isCompleted = completedSectionIndexes.includes(sectionIndex);

            return (
              <button
                key={section.section_index}
                type="button"
                onClick={() => handleSectionTabClick(sectionIndex)}
                className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
                style={{
                  fontWeight: isActive ? 700 : 500,
                  color: "#212529",
                  borderBottom: isActive
                    ? "1px solid #212529"
                    : "1px solid transparent",
                }}
              >
                <span>{t(section.title)}</span>

                {getTomSectionInfo(section.title) && (
                  <>
                    <i
                      className="ri-information-line ms-2 font-size-18 align-middle"
                      id={`tom-section-info-${section.section_index}`}
                      style={{ cursor: "pointer" }}
                    />

                    <UncontrolledTooltip
                      placement="top"
                      target={`tom-section-info-${section.section_index}`}
                    >
                      {t(getTomSectionInfo(section.title))}
                    </UncontrolledTooltip>
                  </>
                )}

                {isCompleted ? (
                  <i
                    className="ri-checkbox-circle-line ms-2 text-success"
                    style={{ fontSize: "18px" }}
                  />
                ) : (
                  <i
                    className="ri-checkbox-blank-circle-line ms-2 text-muted"
                    style={{ fontSize: "18px" }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Active Section Content */}
        <div className="mb-4">
          {(activeSection.subsections || []).map(
            (subsection, subsectionIndex) => (
              <Card
                key={subsection.subsection_index}
                className="mb-4 shadow-sm"
              >
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <Label className="fw-bold mb-1">
                        {subsection.order}. {subsection.title}
                      </Label>

                      {subsection.info ? (
                        <p className="text-muted mb-0">{subsection.info}</p>
                      ) : null}
                    </div>

                    {!!subsection.badge_info && (
                      <span className="badge bg-light text-muted">
                        {subsection.badge_info}
                      </span>
                    )}
                  </div>

                  <FormGroup>
                    <Label>{t("Information")}</Label>
                    <Input
                      type="textarea"
                      rows="4"
                      value={subsection.information || ""}
                      onChange={(e) =>
                        handleInformationChange(
                          activeSectionIndex,
                          subsectionIndex,
                          e.target.value
                        )
                      }
                    />
                  </FormGroup>

                  <Row className="mt-3 mb-3">
                    <Col md={6} sm={12}>
                      <Label>{t("Technical measures")}</Label>

                      <ul className="ps-0 mb-3">
                        {(subsection.technical_measures || []).map(
                          (measure, measureIndex) => (
                            <li
                              key={measureIndex}
                              className="d-flex align-items-center justify-content-between mb-2"
                              style={{ listStyle: "none" }}
                            >
                              <span>{measure}</span>

                              <button
                                type="button"
                                className="btn btn-link text-danger p-0 ms-2"
                                onClick={() =>
                                  handleRemoveMeasure(
                                    activeSectionIndex,
                                    subsectionIndex,
                                    "technical",
                                    measureIndex
                                  )
                                }
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </li>
                          )
                        )}
                      </ul>

                      <div className="d-flex align-items-center">
                        <Input
                          type="text"
                          placeholder={t("Add New")}
                          value={subsection.new_technical_measure || ""}
                          onChange={(e) =>
                            handleNewMeasureInputChange(
                              activeSectionIndex,
                              subsectionIndex,
                              "technical",
                              e.target.value
                            )
                          }
                          onKeyDown={(e) =>
                            handleMeasureInputKeyDown(
                              e,
                              activeSectionIndex,
                              subsectionIndex,
                              "technical"
                            )
                          }
                        />
                        <Button
                          type="button"
                          color="primary"
                          className="ms-2 w-25"
                          onClick={() =>
                            handleAddMeasure(
                              activeSectionIndex,
                              subsectionIndex,
                              "technical"
                            )
                          }
                        >
                          {t("Add New")}
                        </Button>
                      </div>
                    </Col>

                    <Col md={6} sm={12}>
                      <Label>{t("Organizational measures")}</Label>

                      <ul className="ps-0 mb-3">
                        {(subsection.organizational_measures || []).map(
                          (measure, measureIndex) => (
                            <li
                              key={measureIndex}
                              className="d-flex align-items-center justify-content-between mb-2"
                              style={{ listStyle: "none" }}
                            >
                              <span>{measure}</span>

                              <button
                                type="button"
                                className="btn btn-link text-danger p-0 ms-2"
                                onClick={() =>
                                  handleRemoveMeasure(
                                    activeSectionIndex,
                                    subsectionIndex,
                                    "organizational",
                                    measureIndex
                                  )
                                }
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </li>
                          )
                        )}
                      </ul>

                      <div className="d-flex align-items-center">
                        <Input
                          type="text"
                          placeholder={t("Add New")}
                          value={subsection.new_organizational_measure || ""}
                          onChange={(e) =>
                            handleNewMeasureInputChange(
                              activeSectionIndex,
                              subsectionIndex,
                              "organizational",
                              e.target.value
                            )
                          }
                          onKeyDown={(e) =>
                            handleMeasureInputKeyDown(
                              e,
                              activeSectionIndex,
                              subsectionIndex,
                              "organizational"
                            )
                          }
                        />
                        <Button
                          type="button"
                          color="primary"
                          className="ms-2 w-25"
                          onClick={() =>
                            handleAddMeasure(
                              activeSectionIndex,
                              subsectionIndex,
                              "organizational"
                            )
                          }
                        >
                          {t("Add New")}
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <FormGroup className="mt-3">
                    <Label>{t("Description")}</Label>
                    <Input
                      type="textarea"
                      rows="5"
                      value={subsection.description || ""}
                      onChange={(e) =>
                        handleDescriptionChange(
                          activeSectionIndex,
                          subsectionIndex,
                          e.target.value
                        )
                      }
                    />
                  </FormGroup>
                </CardBody>
              </Card>
            )
          )}
        </div>

        {/* Section Navigation Buttons */}
        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              if (activeSectionIndex === 0) {
                setActiveTab("basic-information");
                return;
              }

              setActiveSectionIndex((prev) => prev - 1);
            }}
          >
            {t("Back")}
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSectionNext}
          >
            {activeSectionIndex === editableSections.length - 1
              ? t("Finish")
              : t("Next")}
          </button>
        </div>
      </>
    );
  };

  const renderFinalize = () => {
    if (createTomMutation.isLoading) {
      return (
        <div className="text-center py-4">
          <Spinner color="primary" />
          <p className="mt-3 mb-0">
            {t("Thank you for your patience while your TOM is being created.")}
          </p>
        </div>
      );
    }

    if (createTomMutation.isSuccess) {
      return (
        <>
          <Alert
            color="success"
            className="alert-dismissible fade show"
            role="alert"
          >
            <i className="mdi mdi-check-all me-2"></i>
            <b>{t("TOM created successfully.")}</b>
          </Alert>

          <div className="d-flex gap-2">
            <Link
              className="btn btn-primary"
              to="/admin/data-protection/toms?tab=list"
            >
              {t("Back to TOM list")}
            </Link>
          </div>
        </>
      );
    }

    if (createTomMutation.isError) {
      return (
        <Alert
          color="danger"
          className="alert-dismissible fade show"
          role="alert"
        >
          <i className="mdi mdi-close-circle-outline me-2"></i>
          <b>{t("Error!")}</b>
          <p className="mb-0">
            {t("An error occurred while creating the TOM.")}
          </p>
        </Alert>
      );
    }

    return (
      <p>{t("Thank you for your patience while your TOM is being created.")}</p>
    );
  };

  useEffect(() => {
    if (tomDefaults) {
      const sections = tomDefaults.sections || [];

      setEditableSections(
        sections.map((section) => ({
          ...section,
          subsections: (section.subsections || []).map((subsection) => {
            const measures = subsection.measures || [];

            return {
              ...subsection,
              information: subsection.information || "",
              description: subsection.description || "",
              technical_measures: measures
                .filter((measure) => measure.type === "technical")
                .map((measure) => measure.title || ""),
              organizational_measures: measures
                .filter((measure) => measure.type === "organizational")
                .map((measure) => measure.title || ""),
              new_technical_measure: "",
              new_organizational_measure: "",
            };
          }),
        }))
      );

      setActiveSectionIndex(0);
      setCompletedSectionIndexes([]);
    }
  }, [tomDefaults]);

  useEffect(() => {
    if (activeTab === "finalize" && !hasSubmitted) {
      setHasSubmitted(true);
      createTomMutation.mutate(buildCreatePayload());
    }
  }, [activeTab, hasSubmitted]);

  useEffect(() => {
    if (activeTab !== "finalize") {
      setHasSubmitted(false);
    }
  }, [activeTab]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-4">
          <Col sm="12">
            <Navigator
              backButtonOptions={() => {
                return {
                  enable: true,
                  onClick: () => history.goBack(),
                  title: t("Back"),
                  icon: <i className="ri-arrow-left-fill"></i>,
                };
              }}
              breadCrumbs={[
                { title: "SIP", link: "/" },
                {
                  title: t("TOMs"),
                  link: `/admin/data-protection/toms`,
                },
              ]}
            />
          </Col>
        </Row>

        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <TwitterWizard
                  activeTab={activeTab}
                  key={wizardSteps.map((step) => step.key).join("-")}
                >
                  {wizardSteps?.map((step) => (
                    <TwitterWizardNavItem uniqueKey={step.key} key={step.key}>
                      <span className="step-number">{step.number}</span>
                      <span className="step-title">{step.title}</span>
                    </TwitterWizardNavItem>
                  ))}
                </TwitterWizard>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col sm={12}>
            <Card>
              <CardBody className="smartintegrity__questionnaire__tabs_progress">
                {activeTab === "basic-information" && renderBasicInfo()}
                {activeTab === "edit-texts" && renderEditTexts()}
                {activeTab === "finalize" && renderFinalize()}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const mapStatetoProps = (state) => {
  const { token, user } = state.Login;
  const { Organization } = state;

  return {
    token,
    user,
    Organization,
  };
};

export default withNamespaces()(
  connect(mapStatetoProps)(withRouter(DataProtectionCreateTOM))
);
